import { api } from '@/lib/axios'

/**
 * AP-42 — Mandantenende in der Betreiber-Konsole: Gesamtexport, Vormerkung,
 * endgültige Löschung, Protokoll. Alles hinter dem `PlatformAdminGuard`.
 */

export type EndeAktion =
  | 'EXPORT'
  | 'MARKED'
  | 'WITHDRAWN'
  | 'DELETION_STARTED'
  | 'DELETION_COMPLETED'
  | 'DELETION_FAILED'

export interface EndeProtokollZeile {
  id: string
  tenantRef: string
  tenantName: string
  subdomain: string
  action: EndeAktion
  details: Record<string, unknown> | null
  operatorEmail: string | null
  createdAt: string
}

/** Ein Speicher des Kunden, ohne Zugangsdaten. `endpoint: null` = Amazon S3. */
export interface EigenerSpeicherOrt {
  endpoint: string | null
  bucket: string
}

/** Was der Gesamtexport aus dem eigenen Speicher mitnimmt. */
export type ExportDateien = 'MIT' | 'VERZEICHNIS'

export interface EndeStand {
  tenant: { id: string; name: string; subdomain: string; isActive: boolean; isOperator: boolean }
  vormerkung: {
    am: string
    durch: 'MANDANT' | 'BETREIBER'
    grund: string | null
    fruehestens: string
    spaetestens: string
    wartefristVorbei: boolean
    ueberfaellig: boolean
  } | null
  wartefristTage: number
  loeschfristTage: number
  bestand: {
    benutzer: number
    kunden: number
    projekte: number
    zeitbuchungen: number
    belege: number
    dateien: number
    dateiBytes: number
  }
  speicherBereit: boolean
  /**
   * AP-11-E5: Die eigenen Speicher des Mandanten — leer, wenn alles bei Symphenia liegt.
   * Was dort liegt, bleibt beim Löschen beim Kunden; der Export fragt, ob es mitkommen soll.
   */
  eigenerSpeicher: EigenerSpeicherOrt[]
  letzterExportAm: string | null
  protokoll: EndeProtokollZeile[]
}

export interface EndeGeloescht {
  tenantRef: string
  name: string
  subdomain: string
  dateien: number
  zeilen: number
  anmeldekonten: number
  /** AP-11-E5: Speicher des Kunden, in denen die Dateien unangetastet liegen geblieben sind. */
  beimKundenBelassen?: EigenerSpeicherOrt[]
}

export const mandantenendeApi = {
  stand: async (tenantId: string): Promise<EndeStand> => {
    const res = await api.get<{ data: EndeStand }>(`/admin/tenants/${tenantId}/end`)
    return res.data.data
  },

  /** Lädt den Gesamtexport und nennt den Dateinamen aus der Antwort. */
  gesamtexport: async (tenantId: string, dateien?: ExportDateien): Promise<{ daten: ArrayBuffer; dateiname: string }> => {
    const res = await api.get<ArrayBuffer>(`/admin/tenants/${tenantId}/full-export`, {
      // Nur für Mandanten mit eigenem Speicher: Dateien mitnehmen oder nur das Verzeichnis.
      ...(dateien ? { params: { dateien } } : {}),
      responseType: 'arraybuffer',
      // Ein Betrieb mit vielen Dateien braucht länger als eine gewöhnliche Anfrage.
      timeout: 0,
    })
    const kopf = String(res.headers['content-disposition'] ?? '')
    const dateiname = /filename="?([^";]+)"?/.exec(kopf)?.[1] ?? 'symphenia-gesamtexport.zip'
    return { daten: res.data, dateiname }
  },

  vormerken: async (tenantId: string, reason: string): Promise<EndeStand> => {
    const res = await api.post<{ data: EndeStand }>(`/admin/tenants/${tenantId}/end/mark`, { reason })
    return res.data.data
  },

  zuruecknehmen: async (tenantId: string): Promise<EndeStand> => {
    const res = await api.post<{ data: EndeStand }>(`/admin/tenants/${tenantId}/end/withdraw`)
    return res.data.data
  },

  loeschen: async (
    tenantId: string,
    eingabe: { confirmSubdomain: string; password: string; shortenReason?: string },
  ): Promise<EndeGeloescht> => {
    const res = await api.post<{ data: EndeGeloescht }>(`/admin/tenants/${tenantId}/end/delete`, eingabe)
    return res.data.data
  },

  protokoll: async (page = 1): Promise<{ data: EndeProtokollZeile[]; meta: { total: number; page: number; totalPages: number } }> => {
    const res = await api.get<{ data: EndeProtokollZeile[]; meta: { total: number; page: number; totalPages: number } }>(
      '/admin/tenant-end-log',
      { params: { page, limit: 50 } },
    )
    return res.data
  },
}

export const AKTION_TEXT: Record<EndeAktion, string> = {
  EXPORT: 'Gesamtexport erstellt',
  MARKED: 'Löschung vorgemerkt',
  WITHDRAWN: 'Vormerkung zurückgenommen',
  DELETION_STARTED: 'Löschung begonnen',
  DELETION_COMPLETED: 'Endgültig gelöscht',
  DELETION_FAILED: 'Löschung gescheitert',
}

const zahl = (n: unknown) => (typeof n === 'number' ? n.toLocaleString('de-DE') : '0')

/** „Bucket „x“ bei Amazon S3“ — für Protokoll und Maske. */
export function ortText(orte: EigenerSpeicherOrt[]): string {
  return orte.map((o) => `Bucket „${o.bucket}“ bei ${o.endpoint ?? 'Amazon S3'}`).join(', ')
}

function eigenerSpeicherText(e: unknown): string {
  if (!e || typeof e !== 'object') return ''
  const x = e as { modus?: string; dateien?: number }
  return x.modus === 'MIT'
    ? ` — dazu ${zahl(x.dateien)} Dateien aus dem eigenen Speicher`
    : ' — eigener Speicher nur als Verzeichnis'
}

/** Was ein Protokolleintrag in einem Satz sagt. */
export function protokollText(z: EndeProtokollZeile): string {
  const d = z.details ?? {}
  switch (z.action) {
    case 'EXPORT':
      return (
        `${zahl(d.zeilen)} Datensätze, ${zahl(d.dateien)} Dateien` +
        (d.speicherBereit === false ? ' — ohne Dateispeicher' : '') +
        eigenerSpeicherText(d.eigenerSpeicher)
      )
    case 'MARKED':
      return typeof d.grund === 'string' ? d.grund : ''
    case 'DELETION_STARTED':
      return typeof d.wartefristVerkuerzt === 'string' ? `Wartefrist verkürzt: ${d.wartefristVerkuerzt}` : ''
    case 'DELETION_COMPLETED':
      return (
        `${zahl(d.zeilen)} Datensätze, ${zahl(d.dateien)} Dateien, ${zahl(d.anmeldekonten)} Anmeldekonten` +
        (Array.isArray(d.beimKundenBelassen) && d.beimKundenBelassen.length
          ? ` — Dateien im eigenen Speicher (${ortText(d.beimKundenBelassen as EigenerSpeicherOrt[])}) beim Kunden belassen`
          : '')
      )
    case 'DELETION_FAILED':
      return `Schritt ${String(d.schritt ?? '?')}: ${String(d.meldung ?? '')}`
    default:
      return ''
  }
}
